/**
 * Button components with theme support (light/dark)
 * Accent colors and hover effects adapt to current theme
 */

import { forwardRef } from 'react'
import { Link } from 'react-router-dom'

/**
 * Base button component
 */
export const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    icon,
    iconPosition = 'left',
    className = '',
    as,
    href,
    to,
    ...props
  },
  ref
) {
  const variantClasses = {
    primary: `
      bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black font-semibold
      hover:bg-primary-700 dark:hover:bg-lume-green hover:shadow-md dark:hover:shadow-lume
      active:bg-primary-800 dark:active:bg-lume-green
      disabled:bg-primary-300 dark:disabled:bg-lume-dim disabled:text-white/70 dark:disabled:text-dial-black/50
      focus-visible:ring-primary-500 dark:focus-visible:ring-lume-teal
    `,
    secondary: `
      bg-gray-100 dark:bg-dial-elevated text-gray-900 dark:text-indices-bright border border-gray-200 dark:border-dial-border
      hover:bg-gray-200 dark:hover:bg-dial-surface hover:border-gray-300 dark:hover:border-indices-muted
      active:bg-gray-300 dark:active:bg-dial-black
      disabled:bg-gray-50 dark:disabled:bg-dial-surface disabled:text-gray-400 dark:disabled:text-indices-muted disabled:border-gray-100 dark:disabled:border-dial-border
      focus-visible:ring-gray-400 dark:focus-visible:ring-indices-muted
    `,
    ghost: `
      bg-transparent text-gray-600 dark:text-indices-medium
      hover:bg-gray-100 dark:hover:bg-dial-surface hover:text-gray-900 dark:hover:text-indices-bright
      active:bg-gray-200 dark:active:bg-dial-elevated
      disabled:text-gray-300 dark:disabled:text-indices-recessed
      focus-visible:ring-gray-400 dark:focus-visible:ring-indices-muted
    `,
    danger: `
      bg-status-error/10 dark:bg-status-error/20 text-status-error border border-status-error/20 dark:border-status-error/30
      hover:bg-status-error/20 dark:hover:bg-status-error/30 hover:border-status-error/40 dark:hover:border-status-error/50
      active:bg-status-error/30 dark:active:bg-status-error/40
      disabled:bg-status-error/5 dark:disabled:bg-status-error/10 disabled:text-status-error/50
      focus-visible:ring-status-error
    `,
    success: `
      bg-status-success/10 dark:bg-status-success/20 text-status-success border border-status-success/20 dark:border-status-success/30
      hover:bg-status-success/20 dark:hover:bg-status-success/30 hover:border-status-success/40 dark:hover:border-status-success/50
      active:bg-status-success/30 dark:active:bg-status-success/40
      disabled:bg-status-success/5 dark:disabled:bg-status-success/10 disabled:text-status-success/50
      focus-visible:ring-status-success
    `,
    warning: `
      bg-status-warning/10 dark:bg-status-warning/20 text-status-warning border border-status-warning/20 dark:border-status-warning/30
      hover:bg-status-warning/20 dark:hover:bg-status-warning/30 hover:border-status-warning/40 dark:hover:border-status-warning/50
      active:bg-status-warning/30 dark:active:bg-status-warning/40
      disabled:bg-status-warning/5 dark:disabled:bg-status-warning/10 disabled:text-status-warning/50
      focus-visible:ring-status-warning
    `,
    outline: `
      bg-transparent text-gray-700 dark:text-indices-bright border border-gray-300 dark:border-dial-border
      hover:bg-gray-50 dark:hover:bg-dial-surface hover:border-gray-400 dark:hover:border-indices-muted
      active:bg-gray-100 dark:active:bg-dial-elevated
      disabled:bg-transparent disabled:text-gray-300 dark:disabled:text-indices-muted disabled:border-gray-200 dark:disabled:border-dial-border
      focus-visible:ring-gray-400 dark:focus-visible:ring-indices-muted
    `,
    outlinePrimary: `
      bg-transparent text-primary-600 dark:text-lume-teal border border-primary-300 dark:border-lume-teal/50
      hover:bg-primary-50 dark:hover:bg-lume-teal/10 hover:border-primary-400 dark:hover:border-lume-teal
      active:bg-primary-100 dark:active:bg-lume-teal/20
      disabled:bg-transparent disabled:text-primary-300 dark:disabled:text-lume-dim disabled:border-primary-200 dark:disabled:border-lume-dim
      focus-visible:ring-primary-500 dark:focus-visible:ring-lume-teal
    `,
    link: `
      bg-transparent text-primary-600 dark:text-lume-teal
      hover:text-primary-700 dark:hover:text-lume-green hover:underline
      active:text-primary-800 dark:active:text-lume-cream
      disabled:text-gray-400 dark:disabled:text-indices-muted
      p-0
    `,
  }

  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs rounded-lg',
    sm: 'px-3 py-2 text-sm rounded-lg',
    md: 'px-4 py-2.5 text-sm rounded-xl',
    lg: 'px-5 py-3 text-base rounded-xl',
    xl: 'px-6 py-3.5 text-lg rounded-xl',
  }

  const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-medium
    transition-all duration-150
    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-dial-black
    active:scale-[0.98]
    disabled:cursor-not-allowed disabled:active:scale-100
    ${fullWidth ? 'w-full' : ''}
    ${variantClasses[variant]}
    ${variant !== 'link' ? sizeClasses[size] : ''}
  `

  const isDisabled = disabled || loading

  const content = (
    <>
      {loading ? (
        <svg
          className="animate-spin w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon && iconPosition === 'left' ? (
        <span className="flex-shrink-0">{icon}</span>
      ) : null}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </>
  )

  // Link with `to` prop (react-router)
  if (to && !isDisabled) {
    return (
      <Link
        ref={ref}
        to={to}
        className={`${baseClasses} ${className}`}
        {...props}
      >
        {content}
      </Link>
    )
  }

  // External link with `href`
  if (href && !isDisabled) {
    return (
      <a
        ref={ref}
        href={href}
        className={`${baseClasses} ${className}`}
        {...props}
      >
        {content}
      </a>
    )
  }

  // Custom component
  if (as) {
    const Component = as
    return (
      <Component
        ref={ref}
        className={`${baseClasses} ${className}`}
        disabled={isDisabled}
        {...props}
      >
        {content}
      </Component>
    )
  }

  // Default button
  return (
    <button
      ref={ref}
      type={props.type || 'button'}
      className={`${baseClasses} ${className}`}
      disabled={isDisabled}
      {...props}
    >
      {content}
    </button>
  )
})

/**
 * Icon-only button
 */
export const IconButton = forwardRef(function IconButton(
  {
    children,
    variant = 'ghost',
    size = 'md',
    rounded = false,
    label,
    className = '',
    ...props
  },
  ref
) {
  const variantClasses = {
    ghost: `
      text-gray-500 dark:text-indices-medium
      hover:bg-gray-100 dark:hover:bg-dial-surface hover:text-gray-700 dark:hover:text-indices-bright
      active:bg-gray-200 dark:active:bg-dial-elevated
      disabled:text-gray-300 dark:disabled:text-indices-recessed
      focus-visible:ring-gray-400 dark:focus-visible:ring-indices-muted
    `,
    primary: `
      bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black
      hover:bg-primary-700 dark:hover:bg-lume-green hover:shadow-md dark:hover:shadow-lume
      active:bg-primary-800 dark:active:bg-lume-green
      disabled:bg-primary-300 dark:disabled:bg-lume-dim
      focus-visible:ring-primary-500 dark:focus-visible:ring-lume-teal
    `,
    secondary: `
      bg-gray-100 dark:bg-dial-elevated text-gray-700 dark:text-indices-bright border border-gray-200 dark:border-dial-border
      hover:bg-gray-200 dark:hover:bg-dial-surface hover:border-gray-300 dark:hover:border-indices-muted
      active:bg-gray-300 dark:active:bg-dial-black
      disabled:text-gray-300 dark:disabled:text-indices-muted
      focus-visible:ring-gray-400 dark:focus-visible:ring-indices-muted
    `,
    danger: `
      text-status-error
      hover:bg-status-error/10 dark:hover:bg-status-error/20
      active:bg-status-error/20 dark:active:bg-status-error/30
      disabled:text-status-error/50
      focus-visible:ring-status-error
    `,
  }

  const sizeClasses = {
    xs: 'p-1',
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  }

  return (
    <button
      ref={ref}
      type="button"
      className={`
        inline-flex items-center justify-center
        ${rounded ? 'rounded-full' : 'rounded-lg'}
        transition-all duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-dial-black
        active:scale-95
        disabled:cursor-not-allowed disabled:active:scale-100
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  )
})

/**
 * Button group for grouped actions
 */
export function ButtonGroup({ children, className = '' }) {
  return (
    <div className={`inline-flex rounded-xl overflow-hidden border border-gray-200 dark:border-dial-border ${className}`}>
      {children}
    </div>
  )
}

/**
 * Button group item
 */
export const ButtonGroupItem = forwardRef(function ButtonGroupItem(
  { children, active = false, className = '', ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={`
        px-4 py-2 text-sm font-medium
        border-r border-gray-200 dark:border-dial-border last:border-r-0
        transition-colors
        ${
          active
            ? 'bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black'
            : 'bg-white dark:bg-dial-surface text-gray-700 dark:text-indices-bright hover:bg-gray-50 dark:hover:bg-dial-elevated'
        }
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  )
})

/**
 * Floating action button (FAB)
 */
export const FloatingButton = forwardRef(function FloatingButton(
  {
    children,
    position = 'bottom-right',
    className = '',
    ...props
  },
  ref
) {
  const positionClasses = {
    'bottom-right': 'fixed bottom-20 right-4',
    'bottom-left': 'fixed bottom-20 left-4',
    'bottom-center': 'fixed bottom-20 left-1/2 -translate-x-1/2',
  }

  return (
    <button
      ref={ref}
      type="button"
      className={`
        ${positionClasses[position]}
        w-14 h-14 rounded-full
        bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black shadow-lg dark:shadow-lume
        flex items-center justify-center
        hover:bg-primary-700 dark:hover:bg-lume-green hover:shadow-xl dark:hover:shadow-lume-strong
        active:bg-primary-800 dark:active:bg-lume-green active:scale-95
        transition-all
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:focus-visible:ring-lume-teal focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-dial-black
        z-40
        ${className}
      `}
      {...props}
    >
      {children}
    </button>
  )
})

/**
 * Back button - commonly used in headers
 */
export function BackButton({ onClick, className = '' }) {
  return (
    <IconButton
      onClick={onClick}
      variant="ghost"
      label="Go back"
      className={className}
    >
      <svg
        className="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </IconButton>
  )
}

export default {
  Button,
  IconButton,
  ButtonGroup,
  ButtonGroupItem,
  FloatingButton,
  BackButton,
}
